/**
 * ⚠️ LEGACY — being retired. On prod `www.outdoorsy.com`, `/help/*` is served by the
 * `outdoorsy-renter-cms` app via the `renter-cms-proxy` Cloudflare Worker, NOT this file.
 * Edits here likely have NO production effect — change help behavior in `outdoorsy-renter-cms`
 * (`src/app/help/[slug]/*`). Removed from renter after the Wagtail→Sanity migration. See
 * `src/pages/help/README.md`.
 */
import { GetStaticProps, NextPage } from 'next';
import React from 'react';

import { FAQsStructuredData } from '@/components/localities/FAQs/FAQsStructuredData';
import HelpTemplate from '@/components/templates/HelpTemplate';
import { ARTICLE_SLUG_URL } from '@/constants/urls';
import {
  fetchArticles,
  fetchSidebarLinks,
  IArticleTags,
  IPageCategory,
} from '@/services/getHelpPageData';
import { IFAQItem } from '@/services/types/blog/post.types';

interface IProps {
  title: string;
  content: string;
  slug: string;
  faqItems: IFAQItem[];
  articles: IPageCategory[];
  articleCategories: IPageCategory[];
  articleTags: IArticleTags[];
  categoryTags: IArticleTags[];
}

const Terms: NextPage<IProps> = ({
  title,
  content,
  articles,
  slug,
  faqItems,
  articleCategories,
  articleTags,
  categoryTags,
}) => {
  return (
    <>
      {faqItems.length > 0 && <FAQsStructuredData items={faqItems} />}
      <HelpTemplate
        content={content}
        slug={slug}
        title={title}
        articles={articles}
        articleCategories={articleCategories}
        articleTags={articleTags}
        categoryTags={categoryTags}
      />
    </>
  );
};

export async function getStaticPaths() {
  return {
    paths: [
      { params: { slug: 'privacy-policy' } },
      { params: { slug: 'outdoorsy-terms-and-conditions' } },
    ],
    fallback: 'blocking',
  };
}

export const getStaticProps: GetStaticProps<IProps> = async ({ params }) => {
  const { slug } = params || {};
  if (typeof slug !== 'string') {
    return { notFound: true, revalidate: 86400 };
  }

  const [articlesResp, sidebarResp] = await Promise.all([
    fetchArticles(`${ARTICLE_SLUG_URL}${slug}`),
    fetchSidebarLinks(),
  ]);

  if (articlesResp.fetchArticlesError || sidebarResp.fetchSidebarLinksError) {
    return { notFound: true, revalidate: 86400 };
  }
  const article = articlesResp.articleList?.[0];
  if (!article) {
    return { notFound: true, revalidate: 86400 };
  }

  return {
    props: {
      title: article.title ?? '',
      content: article.content ?? '',
      slug,
      // FAQ schema isn't modeled on the Sanity helpArticle yet; pages that
      // had Yoast FAQs in WP render without the structured-data block until
      // it's added to the schema and the importer backfills it.
      faqItems: [],
      articles: sidebarResp.sidebarLinks?.popArticles ?? [],
      articleCategories: sidebarResp.sidebarLinks?.helpCategories ?? [],
      articleTags: article.tags ?? [],
      categoryTags:
        article.categories?.map(c => ({
          id: c.id != null ? String(c.id) : null,
          name: c.name ?? null,
          slug: c.slug ?? null,
        })) ?? [],
    },
    revalidate: 86400,
  };
};

export default Terms;
